import { Grid, Checkbox, Radio, RadioGroup, Tooltip, Button, TextField, Box, Snackbar, Backdrop, CircularProgress } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { Fragment, useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import getASOConfigPlugInData from "../../state/actionCreators/getASOConfigPlugInData";
import { RootState, AppDispatch } from "../../state/store";
import { IDeliveryType, IPackage } from "../../models/configuration/plugin/IConfiguration";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { REQUEST_TYPES } from "../../utils/constants";
import {
    setOrderPackageId,
    setOrderAltPackageId,
    setOrderParentPackageId,
    setOrderSelectedDeliveryMethods,
    setLoadingState,
    setDefaultPackageId,
    setConfigId
} from "../../state/appSlice";
import {
    StyledMainContainer,
    StyledContentContainer,
    StyledPackageCard,
    StyledDeliveryCard,
    StyledPackageCardContent,
    StyledPackageCardHeader,
    StyledDeliveryCardHeader,
    StyledDeliveryCardContent,
    StyledPackageFormControl,
    StyledPackageTooltip,
    StyledPackageFormControlLabel,
    StyledDeliveryFormGroup,
    StyledDeliveryFormControlLabel
} from '../Order.styles';
import TransactionBuilderService from "../../services/transactionBuilder";
import ElliHost from "../../services/elliHost";
import { AlertSeverity } from "../../constants/AlertTypes";
import { DtStatusDialog } from "../../components/ui/dialog";
import DtAlert from "../../components/ui/alert/DtAlert";
import { SpecialDeliveryTypes, DeliveryTypes, FullfillmentShippingOptions, SpecialClosingTypes } from "../../enums/SpecialDeliveryTypes";

// Generalizes the historical "push the one fixed secondary Id" behavior: for each of the
// eSign/eDisclose/eClose parents present in deliveryTypes, expand to every configured delivery
// type Id under that parent (not just the single fixed constant), so dynamically-added secondary
// methods (e.g. "eSign-a1b2c3d4-...") become selectable alongside the fixed ones. Implemented independently
// from Order.tsx's equivalent function (not shared) since each component reads from its own
// Redux selector. Mutates and returns deliveryTypes in place to match the existing push-style
// call sites in this file.
export const expandSecondaryDeliveryTypes = (
    deliveryTypes: string[],
    configuredDeliveryTypes: IDeliveryType[] | null | undefined
): string[] => {
    if (!configuredDeliveryTypes?.length) return deliveryTypes;

    [DeliveryTypes.ESIGN, DeliveryTypes.EDISCLOSE, DeliveryTypes.ECLOSE].forEach(parent => {
        const parentLower = parent.toLowerCase();
        if (deliveryTypes.some(dt => dt?.toLowerCase().startsWith(parentLower))) {
            configuredDeliveryTypes.forEach(cdt => {
                const idLower = cdt.Id.toLowerCase();
                const alreadyIncluded = deliveryTypes.some(dt => dt?.toLowerCase() === idLower);
                if (idLower.startsWith(parentLower) && idLower !== parentLower && !alreadyIncluded) {
                    deliveryTypes.push(cdt.Id);
                }
            });
        }
    });

    return deliveryTypes;
};

const OrderTemplatePage: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const appState = useSelector((state: RootState) => state.appSlice);
    const packageTypeState = useTypedSelector((state) => state.configASOPluginData.configData?.Packages ?? null);
    const deliveryTypeState = useTypedSelector((state) => state.configASOPluginData.configData?.DeliveryTypes ?? null);
    const configMisc = useTypedSelector((state) => state.configASOPluginData.configData?.DefaultValues);
    const asoConfigError = useTypedSelector((state) => state.configASOPluginData.error);
    const asoConfigLoading = useTypedSelector((state) => state.configASOPluginData.loading);

    const [packageTypes, setPackageTypes] = useState<Array<IPackage>>([]);
    const [deliveryMethods, setDeliveryMethods] = useState<Array<IDeliveryType>>([]);
    const [selectedDeliveryMethods, setSelectedDeliveryMethods] = useState<Array<IDeliveryType>>([]);
    const [packageId, setPackageId] = useState('');
    const [altPackageId, setAltPackageId] = useState('');
    const [parentPackageId, setParentPackageId] = useState('');
    const [templateName, setTemplateName] = useState('');
    const [isPackageFormControlHovered, setIsPackageFormControlHovered] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [defaultDeliveryMethods, setDefaultDeliveryMethods] = useState<string[]>([]);
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); };
    const [configErrorOpen, setConfigErrorOpen] = useState(false);
    const globalConfigId = useTypedSelector((state) => state.appSlice.configId);
    const handleCloseIntegration = useCallback(() => {
        setConfigErrorOpen(false);
        const elliHost = ElliHost.getInstance();
        void elliHost.closeTransactionTemplate();
    }, []);

    const requestType = appState.orderState?.requestType || configMisc?.DefaultRequestType || REQUEST_TYPES.LIGHTS_OUT;

    // Helper function to expand special delivery types (Mail variants, eClose variants, etc.)
    const addSpecialDeliveryTypes = useCallback((deliveryTypes: string[]) => {
        let updatedDeliveryTypes = [...deliveryTypes]; // avoid mutating the array

        // Adding custom delivery
        updatedDeliveryTypes.push(SpecialDeliveryTypes.PrintInPerson);

        // Adding special delivery types for FullfillmentShippingOptions
        if (updatedDeliveryTypes.includes(DeliveryTypes.MAIL)) {
            for (let deliveryType in FullfillmentShippingOptions) {
                updatedDeliveryTypes.push(`${DeliveryTypes.MAIL}-${deliveryType}`);
            }
        }

        // Adding special delivery types for SpecialClosingTypes
        if (updatedDeliveryTypes.includes(DeliveryTypes.ECLOSE)) {
            for (let deliveryType in SpecialClosingTypes) {
                updatedDeliveryTypes.push(`${DeliveryTypes.ECLOSE}-${deliveryType}`);
            }
        }

        return expandSecondaryDeliveryTypes(updatedDeliveryTypes, deliveryTypeState);
    }, [deliveryTypeState]);

    // Fetch ASO Plugin Config on initial load
    const configFetchedForNull = useRef(false);
    useEffect(() => {
        if (!globalConfigId && configFetchedForNull.current) return;
        dispatch<any>(getASOConfigPlugInData((globalConfigId ?? '').toString()));
        
        if (!globalConfigId) {
            configFetchedForNull.current = true;
        }
    }, [globalConfigId, dispatch]);

    useEffect(() => {
        if (!asoConfigError) return;
        setConfigErrorOpen(true);
    }, [asoConfigError]);

    // On page load fetch current transaction template
    const asoExistingFetchedRef = useRef(false);
    useEffect(() => {
        if (asoExistingFetchedRef.current) return;
        asoExistingFetchedRef.current = true;
        const fetchTemplate = async () => {
            try {
                const elliHost = ElliHost.getInstance();
                const tpl = await elliHost.getCurrentTransactionTemplate();
                setTemplateName(tpl?.name || '');

                const docGenRequest = tpl?.request?.options?.DocGenRequest;
                const configurationId = docGenRequest?.ConfigurationId || '';
                const pkgId = docGenRequest?.RequestOptions?.PackageType || ''

                // Set global configuration ID
                dispatch(setConfigId(configurationId));

                // Initialize packageId and default delivery methods from the template
                setPackageId(pkgId || '');
                dispatch(setOrderPackageId(pkgId || ''));
                dispatch(setDefaultPackageId(pkgId || ''));

                const defDeliveryMethodsRaw: any = tpl?.request?.options?.CustomAttributes?.CxDeliveryType ?? [];
                const defDeliveryMethods: string[] = Array.isArray(defDeliveryMethodsRaw)
                    ? defDeliveryMethodsRaw
                    : typeof defDeliveryMethodsRaw === 'string'
                        ? (defDeliveryMethodsRaw.includes(',')
                            ? defDeliveryMethodsRaw.split(',').map((s: string) => s.trim()).filter(Boolean)
                            : [defDeliveryMethodsRaw])
                        : [];
                setDefaultDeliveryMethods(defDeliveryMethods);
            } catch (err) {
                console.error('Failed to get current transaction template:', err);
            }
        };
        fetchTemplate();
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize and filter package types based on selected sidebar page (group)
    useEffect(() => {
        if (!packageTypeState) {
            setPackageTypes([]);
            return;
        }

        const enabledPackages = packageTypeState.filter((p: IPackage) => p.Enabled);
        const currentGroup = (appState.page ?? '').toString();
        const grouped = currentGroup
            ? enabledPackages.filter((p: IPackage) => (p.Group ?? '').toString() === currentGroup)
            : enabledPackages;

        setPackageTypes(grouped);

        // Ensure current selection belongs to the current group; otherwise pick default or first
        const existsInGroup = grouped.some((p: IPackage) => (p.Id ?? '') === packageId);
        if (!existsInGroup) {
            const desired = grouped.find((p: IPackage) => (p.Id ?? '').toString().toLowerCase() === (appState.defaultPackageId ?? '').toString().toLowerCase());
            const newPkg = desired ?? grouped[0];
            const newId = newPkg?.Id ?? '';

            setPackageId(newId);
            setAltPackageId(newPkg?.AlternatePackageId ?? '');
            setParentPackageId(newPkg?.ParentPackageId ?? '');
            dispatch(setOrderPackageId(newId));
            dispatch(setOrderAltPackageId(newPkg?.AlternatePackageId ?? ''));
            dispatch(setOrderParentPackageId(newPkg?.ParentPackageId ?? ''));
        }
    }, [packageTypeState, appState.page, appState.defaultPackageId, packageId, dispatch]);

    // Update delivery methods and apply defaults when inputs change
    useEffect(() => {
        if (!packageId) {
            setDeliveryMethods([]);
            setSelectedDeliveryMethods([]);
            dispatch(setOrderSelectedDeliveryMethods([]));
            return;
        }

        const selectedPkg = packageTypeState?.find((p: IPackage) => p.Id === packageId);
        const validDeliveryTypes = selectedPkg?.ValidDeliveryTypes ?? [];
        
        // Expand special delivery types (e.g., eClose -> eClose-RON, eClose-IPEN, etc.)
        const expandedValidDeliveryTypes = validDeliveryTypes.length > 0 
            ? addSpecialDeliveryTypes(validDeliveryTypes) 
            : [];

        const filteredDeliveryMethods: IDeliveryType[] =
            expandedValidDeliveryTypes && expandedValidDeliveryTypes.length > 0
                ? (deliveryTypeState ?? []).filter((dm: IDeliveryType) =>
                    dm.Enabled &&
                    expandedValidDeliveryTypes.some((v: string | number | undefined) =>
                        (v ?? '').toString().toLowerCase() === (dm.Id ?? '').toString().toLowerCase()
                    )
                )
                : (deliveryTypeState?.filter((dm: IDeliveryType) => dm.Enabled) ?? []);
        
        // Combine and sort by order
        const sortedDeliveryMethods = filteredDeliveryMethods.sort((a, b) => 
            (Number(a.Order ?? 0) - Number(b.Order ?? 0))
        );
        
        setDeliveryMethods(sortedDeliveryMethods);

        const defaultsSource: unknown = defaultDeliveryMethods;
        const defaults = (
            Array.isArray(defaultsSource)
                ? defaultsSource
                : typeof defaultsSource === 'string'
                    ? [defaultsSource]
                    : []
        ).map(d => (d ?? '').toString().toLowerCase());
        if ((appState.defaultPackageId ?? '') === packageId && defaults.length > 0) {
            const selected = sortedDeliveryMethods.filter(dm => defaults.includes((dm.Id ?? '').toString().toLowerCase()));
            setSelectedDeliveryMethods(selected);
            dispatch(setOrderSelectedDeliveryMethods(selected));
        } else {
            setSelectedDeliveryMethods([]);
            dispatch(setOrderSelectedDeliveryMethods([]));
        }
    }, [packageId, packageTypeState, deliveryTypeState, appState.defaultPackageId, defaultDeliveryMethods, dispatch]);


    // Handle delivery method selection
    const handleDeliveryMethodClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const deliveryMethodId = event.target.value;
        const isChecked = event.target.checked;

        const deliveryMethod = deliveryMethods.find(dm => dm.Id === deliveryMethodId);

        if (!deliveryMethod) return;
        let updatedDeliveryMethods: IDeliveryType[];

        if (isChecked) {
            updatedDeliveryMethods = [...selectedDeliveryMethods, deliveryMethod];
        } else {
            updatedDeliveryMethods = selectedDeliveryMethods.filter(dm => dm.Id !== deliveryMethodId);
        }

        setSelectedDeliveryMethods(updatedDeliveryMethods);
        dispatch(setOrderSelectedDeliveryMethods(updatedDeliveryMethods));
    }, [deliveryMethods, selectedDeliveryMethods, dispatch]);

    // Handle save template
    const handleSaveTemplate = async () => {
        const elliHost = ElliHost.getInstance();

        // Build transaction object using the service
        const transactionRequest = TransactionBuilderService.buildTransactionObject({
            requestType: requestType,
            packageId: packageId,
            parentTypeId: parentPackageId,
            altPackageId: altPackageId,
            deliveryMethods: selectedDeliveryMethods
        });

        if (!transactionRequest) {
            console.error('Failed to build transaction object');
            return;
        }

        const trimmedName = (templateName || '').trim();
        if (!trimmedName) {
            return;
        }

        const templateRequest = {
            name: trimmedName,
            request: transactionRequest
        };

        try {
            await elliHost.saveTransactionTemplate(templateRequest);
            await elliHost.closeTransactionTemplate();
        } catch (error) {
            dispatch(setLoadingState({ loading: false }));
            console.error('Failed to save template:', error);
            setAlertSeverity('error');
            setAlertMessage('Failed to save template');
            setAlertOpen(true);
        }
    };

    return (
        <StyledMainContainer alignItems={"start !important"}>
            {(!asoConfigLoading && !configErrorOpen) && (
            <StyledContentContainer maxWidth="xl">
                <Grid container spacing={3} justifyContent="space-between">
                    {/* Package Card */}
                    <Grid item xs={12} md={6} lg={6}>
                        <StyledPackageTooltip
                            show={isPackageFormControlHovered}
                            title={'Choose Package type. Doing so will present valid delivery types for this package'}
                            PopperProps={{ sx: { display: isPackageFormControlHovered ? 'block' : 'none' } }}
                        >
                            <StyledPackageCard className="responsive-card">
                                <StyledPackageCardHeader
                                    title="Package">
                                </StyledPackageCardHeader>
                                <StyledPackageCardContent>
                                    <StyledPackageFormControl>
                                        <RadioGroup
                                            value={packageId}
                                            name="template-package-radio"
                                            sx={{ gap: 0 }}>
                                            {packageTypes.map((p, i) =>
                                                <Fragment key={`packageType-${i}`}>
                                                    <StyledPackageFormControlLabel
                                                        packageColor={!p.ParentPackageId ? undefined : configMisc?.AlternatePackageColor}
                                                        className="form-control-enhanced modern-radio"
                                                        value={p.Id}
                                                        control={<Radio />}
                                                        label={
                                                            <Tooltip title={p.ToolTipText ?? ''}
                                                                PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [50, 0] } }] }}>
                                                                <span>{p.Caption ? p.Caption : p.Id}</span>
                                                            </Tooltip>
                                                        }
                                                        onMouseEnter={() => setIsPackageFormControlHovered(true)}
                                                        onMouseLeave={() => setIsPackageFormControlHovered(false)}
                                                        onChange={() => {
                                                            setPackageId(p.Id ?? "");
                                                            setAltPackageId(p.AlternatePackageId ?? "");
                                                            setParentPackageId(p.ParentPackageId ?? "");
                                                            dispatch(setOrderPackageId(p.Id ?? ""));
                                                            dispatch(setOrderAltPackageId(p.AlternatePackageId ?? ""));
                                                            dispatch(setOrderParentPackageId(p.ParentPackageId ?? ""));
                                                        }}
                                                    />
                                                </Fragment>
                                            )}
                                        </RadioGroup>
                                    </StyledPackageFormControl>
                                </StyledPackageCardContent>
                            </StyledPackageCard>
                        </StyledPackageTooltip>
                    </Grid>

                    {/* Delivery Method Card */}
                    <Grid item xs={12} md={6} lg={6}>
                        <StyledDeliveryCard className="responsive-card">
                            <StyledDeliveryCardHeader
                                requestType={requestType}
                                title="Delivery Method">
                            </StyledDeliveryCardHeader>
                            <StyledDeliveryCardContent>
                                <StyledDeliveryFormGroup>
                                    {deliveryMethods.map((dm, i) =>
                                        <StyledDeliveryFormControlLabel
                                            className="form-control-enhanced modern-checkbox"
                                            control={
                                                <Tooltip title={dm.ToolTipText ?? ''}
                                                    PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [50, 0] } }] }}>
                                                    <Checkbox
                                                        value={dm.Id}
                                                        checked={selectedDeliveryMethods.some(selected => selected.Id === dm.Id)}
                                                        onChange={(e) => handleDeliveryMethodClick(e)}
                                                    />
                                                </Tooltip>
                                            }
                                            label={
                                                <Tooltip title={dm.ToolTipText ?? ''}
                                                    PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [50, 0] } }] }}>
                                                    <span>{dm.Caption ? dm.Caption : dm.Id}</span>
                                                </Tooltip>
                                            }
                                            key={`dmethod-${i}`}
                                        />
                                    )}
                                </StyledDeliveryFormGroup>
                            </StyledDeliveryCardContent>
                        </StyledDeliveryCard>
                    </Grid>

                    {/* Template name input below and outside the Package card */}
                    <Grid item xs={12} md={6} lg={6}>
                        <Box sx={{ mt: 2, position: 'relative', zIndex: 1 }}>
                            <TextField
                                label="Template name"
                                value={templateName}
                                onChange={(e) => {
                                    setTemplateName(e.target.value);
                                }}
                                id="template-name"
                                name="templateName"
                                required
                                size="small"
                                variant="outlined"
                                fullWidth
                            />
                        </Box>
                    </Grid>

                    {/* Save Template Button */}
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={handleSaveTemplate}
                            disabled={!packageId || selectedDeliveryMethods.length === 0 || !(templateName || '').trim()}
                            sx={{ mt: 2 }}
                        >
                            Save Template
                        </Button>
                    </Grid>
                </Grid>
            </StyledContentContainer>
            )}
            <Backdrop
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: "rgba(0, 0, 0, 0.5)" }}
                open={asoConfigLoading && !configErrorOpen}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alertOpen}
                autoHideDuration={8000}
                onClose={handleSnackBarClose}
            >
                <MuiAlert
                    onClose={handleSnackBarClose}
                    severity={alertSeverity}
                    sx={{ width: '100%', fontSize: '1.1rem', padding: '10px 14px' }}>
                    {alertMessage}
                </MuiAlert>
            </Snackbar>
            <DtStatusDialog
                open={configErrorOpen}
                onClose={handleCloseIntegration}
                title="Setup Needed"
                status="error"
                closeLabel="OK"
                isClosable={true}
                message={
                    <Box>
                        <Grid item gap={2}>
                            <DtAlert icon={<></>} severity="error" sx={{ mt: 1 }}>
                                Error Details: {asoConfigError}
                            </DtAlert>
                        </Grid>
                    </Box>
                }
            />
        </StyledMainContainer>
    );
};

export default OrderTemplatePage;
